/**
 * Debrief (08 §2.9): a rubber-stamped result, the score lines typed out one by one, the threat
 * multiplier, total and time left, then RUN IT AGAIN. Runs on timers, not on `update()`, so it
 * completes even if the game loop stops at mission end.
 */

import './debriefOverlay.css';
import type { MissionDef, MissionResult } from '../../sim/types';
import { el, mmss, show } from './dom';
import type { Tone } from './dom';
import { actionsRow, buildOverlay, button } from './overlay';
import { Timers } from './timers';

/** Let the last moment of the mission be seen before the report covers it. */
const DEBRIEF_DELAY_MS = 1200;
const STAMP_SETTLE_MS = 450;
const LINE_INTERVAL_MS = 120;
const INPUT_GUARD_MS = 400;
const KIA_INPUT_DELAY_MS = 2500;

function stampFor(result: MissionResult): { text: string; tone: Tone } {
  switch (result.outcome) {
    case 'extracted':
      return result.objectiveComplete
        ? { text: 'MISSION ACCOMPLISHED', tone: 'green' }
        : { text: 'MISSION FAILED', tone: 'amber' };
    case 'kia':
      return { text: 'KILLED IN ACTION', tone: 'red' };
    case 'captured':
      return { text: 'CAPTURED', tone: 'red' };
    case 'walkedOut':
      return { text: 'MISSING IN ACTION', tone: 'amber' };
    case 'scrubbed':
      return { text: 'MISSION SCRUBBED', tone: 'amber' };
  }
}

function signed(points: number): string {
  const n = Math.round(points);
  const body = Math.abs(n).toLocaleString('en-US');
  return n > 0 ? `+${body}` : n < 0 ? `-${body}` : '0';
}

function scoreRow(label: string, value: string, extraClass = ''): HTMLElement {
  const row = el('div', extraClass === '' ? 'hud-score-row' : `hud-score-row ${extraClass}`);
  row.append(
    el('span', 'hud-score-label', label),
    el('span', 'hud-score-leader'),
    el('span', 'hud-score-points', value),
  );
  return row;
}

export class DebriefOverlay {
  readonly el: HTMLElement;
  private readonly stamp: HTMLElement;
  private readonly score: HTMLElement;
  private readonly again: HTMLButtonElement;
  private readonly timers = new Timers();
  private isOpen = false;

  /** `onToggle` fires after the overlay opens or hides, so the HUD can re-rank its overlays. */
  constructor(
    private readonly def: MissionDef,
    onRestart: () => void,
    private readonly onToggle: () => void,
  ) {
    const { overlay, dialog } = buildOverlay('hud-debrief', 'Debrief');
    const head = el('div', 'hud-dialog-head');
    head.append(
      el('span', 'hud-dialog-head-title', 'AFTER-ACTION REPORT'),
      el('span', 'hud-dialog-head-name', def.name),
    );
    this.stamp = el('div', 'hud-stamp');
    const stampBox = el('div', 'hud-stamp-box');
    stampBox.append(this.stamp);
    this.score = el('div', 'hud-score');
    this.again = button('RUN IT AGAIN', onRestart);
    this.again.disabled = true;
    dialog.append(head, stampBox, this.score, actionsRow(this.again));
    this.el = overlay;
  }

  get open(): boolean {
    return this.isOpen;
  }

  /** Raise the debrief for `result` after the end-of-mission pause. */
  schedule(result: MissionResult): void {
    this.timers.later(DEBRIEF_DELAY_MS, () => this.reveal(result));
  }

  /** Hide it and cancel anything pending, including a scheduled reveal. */
  hide(): void {
    this.timers.clear();
    this.again.disabled = true;
    this.isOpen = false;
    show(this.el, false);
    this.onToggle();
  }

  dispose(): void {
    this.timers.clear();
  }

  private reveal(result: MissionResult): void {
    const stamp = stampFor(result);
    this.stamp.textContent = stamp.text;
    this.stamp.className = `hud-stamp hud-stamp-${stamp.tone}`;
    this.el.classList.toggle('hud-overlay-grave', result.outcome === 'kia');

    const lines = result.lines.map((line) =>
      scoreRow(line.label, signed(line.points), line.points < 0 ? 'hud-score-neg' : ''),
    );
    const tail = [
      scoreRow(`THREAT ${this.def.threat}`, `x${result.multiplier.toFixed(2)}`),
      scoreRow('TOTAL', Math.round(result.total).toLocaleString('en-US'), 'hud-score-total'),
      scoreRow('TIME LEFT', mmss(result.secondsLeft)),
    ];
    this.score.replaceChildren(...lines, el('div', 'hud-score-rule'), ...tail);
    this.again.disabled = true;
    this.isOpen = true;
    show(this.el, true);
    this.onToggle();

    this.timers.later(STAMP_SETTLE_MS, () => this.revealRows([...lines, ...tail], 0));
    // Loss gets ceremony (08 §5): a death cannot be clicked through by a finger still firing.
    this.timers.later(result.outcome === 'kia' ? KIA_INPUT_DELAY_MS : INPUT_GUARD_MS, () => {
      this.again.disabled = false;
      this.again.focus({ preventScroll: true });
    });
  }

  private revealRows(rows: readonly HTMLElement[], index: number): void {
    const row = rows[index];
    if (row === undefined) return;
    row.classList.add('hud-row-in');
    this.timers.later(LINE_INTERVAL_MS, () => this.revealRows(rows, index + 1));
  }
}
